import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import {
  callLeaveRoom, callMoveSelf, callSetRoomRuleset, callSetSeat, callStartMatch,
} from "../lib/functions";
import type { Difficulty } from "../ai/bot";
import type { RoomDoc, SeatOccupant } from "../types/room";
import { DEFAULT_RULESET, type Ruleset } from "../types/game";
import { RULESET_INFO, RULESET_LIST } from "../content/rules";

export default function RoomPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  const [room, setRoom] = useState<RoomDoc | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!roomId) return;
    const unsub = onSnapshot(
      doc(db, "rooms", roomId),
      snap => {
        if (!snap.exists()) {
          setNotFound(true);
          setRoom(null);
          return;
        }
        setRoom({ ...(snap.data() as RoomDoc) });
      },
      err => setError(err.message),
    );
    return unsub;
  }, [roomId]);

  if (!user) return <Navigate to="/" replace />;
  if (!profile) return <Navigate to="/" replace />;

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="bg-white/5 rounded-2xl p-8 max-w-md w-full text-center">
          <h1 className="text-2xl font-bold mb-2">Room not found</h1>
          <p className="text-white/70 mb-4">It may have been closed. Head back to the lobby.</p>
          <button
            onClick={() => navigate("/", { replace: true })}
            className="px-4 py-2 rounded-lg bg-white text-slate-900 font-semibold"
          >
            Home
          </button>
        </div>
      </div>
    );
  }

  if (!room) {
    return <div className="px-6 py-10 text-white/60">Loading room…</div>;
  }

  // Once the host starts the match, everyone's snapshot flips and we redirect.
  // Gate on "playing" (not just matchId) so we wait until startMatch has
  // actually written the match doc — otherwise late joiners / the host
  // might navigate to a match that doesn't exist yet.
  if (room.status === "playing" && room.matchId) {
    return <Navigate to={`/play-online/${room.matchId}`} replace />;
  }

  const isHost = room.hostUid === user.uid;
  const shareUrl = `${location.origin}/join/${room.code}`;
  const occupiedHumans = room.seats.filter(s => s && s.uid).length;
  const allSeatsFilled = room.seats.every(s => s !== null);
  const mySeatIdx = room.seats.findIndex(s => s?.uid === user.uid);
  const amSeated = mySeatIdx !== -1;
  const ruleset: Ruleset = (room.ruleset as Ruleset | undefined) ?? DEFAULT_RULESET;
  const rulesetInfo = RULESET_INFO[ruleset];

  async function handleLeave() {
    if (!roomId) return;
    setBusy(true);
    setError(null);
    try {
      await callLeaveRoom(roomId);
      navigate("/", { replace: true });
    } catch (e: any) {
      setError(e?.message ?? "Failed to leave room.");
      setBusy(false);
    }
  }

  async function handleSetSeat(seatIndex: 0 | 1 | 2 | 3, kind: "bot" | "empty", difficulty?: Difficulty) {
    if (!roomId) return;
    setBusy(true);
    setError(null);
    try {
      await callSetSeat({
        roomId,
        seatIndex,
        kind,
        difficulty: kind === "bot" ? (difficulty ?? "advanced") : difficulty,
      });
    } catch (e: any) {
      setError(e?.message ?? "Failed to update seat.");
    } finally {
      setBusy(false);
    }
  }

  async function handleMoveSelf(seatIndex: 0 | 1 | 2 | 3) {
    if (!roomId) return;
    setBusy(true);
    setError(null);
    try {
      await callMoveSelf({ roomId, seatIndex });
    } catch (e: any) {
      setError(e?.message ?? "Couldn't move to that seat.");
    } finally {
      setBusy(false);
    }
  }

  async function handleChangeRuleset(next: Ruleset) {
    if (!roomId || next === ruleset) return;
    setBusy(true);
    setError(null);
    try {
      await callSetRoomRuleset({ roomId, ruleset: next });
    } catch (e: any) {
      setError(e?.message ?? "Couldn't change the ruleset.");
    } finally {
      setBusy(false);
    }
  }

  async function copyCode() {
    try { await navigator.clipboard.writeText(room!.code); } catch { /* ignore */ }
  }

  async function copyLink() {
    try { await navigator.clipboard.writeText(shareUrl); } catch { /* ignore */ }
  }

  async function handleStart() {
    if (!roomId) return;
    setBusy(true);
    setError(null);
    try {
      await callStartMatch(roomId);
      // Room snapshot will update with `matchId` — the Navigate above picks it up.
    } catch (e: any) {
      console.error("startMatch failed", e);
      const code = e?.code ?? e?.name;
      const msg = e?.message ?? "Failed to start match.";
      setError(code ? `${msg} (${code})` : msg);
      setBusy(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Room lobby</h1>
        <button
          onClick={handleLeave}
          disabled={busy}
          className="text-sm text-white/60 hover:text-white border border-white/15 rounded-lg px-3 py-1.5 disabled:opacity-50"
        >
          Leave
        </button>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6">
        <div className="text-xs uppercase tracking-wide text-white/40 mb-1">Share code</div>
        <div className="flex items-center gap-3 mb-3">
          <div className="font-mono font-bold text-3xl tracking-widest bg-black/30 rounded-lg px-4 py-2">
            {room.code}
          </div>
          <button
            onClick={copyCode}
            className="text-sm bg-white/10 hover:bg-white/20 rounded-lg px-3 py-2"
          >
            Copy code
          </button>
          <button
            onClick={copyLink}
            className="text-sm bg-white/10 hover:bg-white/20 rounded-lg px-3 py-2"
          >
            Copy link
          </button>
        </div>
        <div className="text-xs text-white/50 break-all">{shareUrl}</div>
      </div>

      {/* Ruleset for this match. Host can toggle in the lobby; once the
          match starts it's locked in. */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-4">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <div className="text-xs uppercase tracking-wide text-white/50 mb-0.5">Ruleset</div>
            <div className="font-semibold">{rulesetInfo.label}</div>
            <div className="text-xs text-white/60">{rulesetInfo.tagline}</div>
          </div>
          <Link to="/rules" className="text-xs text-emerald-300 hover:text-emerald-200 whitespace-nowrap">
            Rules FAQ
          </Link>
        </div>
        {isHost ? (
          <div className="grid sm:grid-cols-2 gap-2 mt-2">
            {RULESET_LIST.map(r => {
              const active = r.id === ruleset;
              return (
                <button
                  key={r.id}
                  onClick={() => handleChangeRuleset(r.id)}
                  disabled={busy || active}
                  className={
                    "text-left rounded-xl border px-3 py-2 transition disabled:cursor-default " +
                    (active
                      ? "bg-emerald-500/15 border-emerald-400/50 ring-1 ring-emerald-400/40"
                      : "bg-white/5 border-white/10 hover:bg-white/10 disabled:opacity-60")
                  }
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">{r.shortLabel}</span>
                    {active && <span className="text-[10px] text-emerald-300 font-semibold">selected</span>}
                  </div>
                  <div className="text-xs text-white/60">{r.tagline}</div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="text-xs text-white/40 mt-1">Host picks the ruleset.</div>
        )}
      </div>

      {!amSeated && (
        <div className="mb-4 rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          <span className="font-semibold">Pick a seat</span> — click <span className="font-semibold">Sit here →</span> on Team 1 or Team 2 below to join a team.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <TeamColumn
          teamId="A"
          seats={room.seats}
          seatIndices={[0, 2]}
          isHost={isHost}
          busy={busy}
          selfUid={user.uid}
          onSetSeat={handleSetSeat}
          onMoveSelf={handleMoveSelf}
        />
        <TeamColumn
          teamId="B"
          seats={room.seats}
          seatIndices={[1, 3]}
          isHost={isHost}
          busy={busy}
          selfUid={user.uid}
          onSetSeat={handleSetSeat}
          onMoveSelf={handleMoveSelf}
        />
      </div>

      {error && <div className="text-red-300 text-sm mb-4">{error}</div>}

      <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between gap-4">
        <div className="text-sm text-white/70">
          {occupiedHumans} of {room.seats.filter(s => s !== null).length} seat{occupiedHumans === 1 ? "" : "s"} are humans. The host can fill empty seats with bots.
        </div>
        {isHost ? (
          <button
            onClick={handleStart}
            disabled={busy || !allSeatsFilled}
            title={allSeatsFilled ? undefined : "All four seats must be filled."}
            className="px-5 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? "Starting…" : "Start game"}
          </button>
        ) : (
          <div className="text-sm text-white/50">Waiting for host…</div>
        )}
      </div>

      <div className="mt-4 text-xs text-white/40">
        Partners sit opposite each other. Click any empty or bot seat to move yourself into it.
      </div>
    </div>
  );
}

// Team visual identity — kept consistent between lobby, match, and future
// scoreboards. Team 1 = A (seats 0 & 2). Team 2 = B (seats 1 & 3).
const TEAM_STYLES: Record<"A" | "B", {
  label: string;
  accent: string;        // ring / border accent
  badge: string;         // team-label pill
  tile: string;          // card background when occupied
  tileEmpty: string;     // card background when empty
}> = {
  A: {
    label: "Team 1",
    accent: "ring-emerald-400/60 border-emerald-400/40",
    badge: "bg-emerald-500/20 text-emerald-200 border border-emerald-400/30",
    tile: "bg-emerald-500/10 border-emerald-400/30",
    tileEmpty: "bg-emerald-500/5 border-emerald-400/15 border-dashed",
  },
  B: {
    label: "Team 2",
    accent: "ring-emerald-400/60 border-emerald-400/40",
    badge: "bg-emerald-500/20 text-emerald-200 border border-emerald-400/30",
    tile: "bg-emerald-500/10 border-emerald-400/30",
    tileEmpty: "bg-emerald-500/5 border-emerald-400/15 border-dashed",
  },
};

function TeamColumn({
  teamId, seats, seatIndices, isHost, busy, selfUid, onSetSeat, onMoveSelf,
}: {
  teamId: "A" | "B";
  seats: (SeatOccupant | null)[];
  seatIndices: [0 | 1 | 2 | 3, 0 | 1 | 2 | 3];
  isHost: boolean;
  busy: boolean;
  selfUid: string;
  onSetSeat: (i: 0 | 1 | 2 | 3, kind: "bot" | "empty", difficulty?: Difficulty) => void;
  onMoveSelf: (i: 0 | 1 | 2 | 3) => void;
}) {
  const style = TEAM_STYLES[teamId];
  return (
    <div className={"rounded-2xl border p-4 " + style.accent}>
      <div className="flex items-center justify-between mb-3">
        <span className={"px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide " + style.badge}>
          {style.label}
        </span>
        <span className="text-[10px] text-white/40 uppercase tracking-wide">Partners</span>
      </div>
      <div className="space-y-3">
        {seatIndices.map(i => (
          <SeatCard
            key={i}
            index={i}
            teamId={teamId}
            occupant={seats[i]}
            isHost={isHost}
            busy={busy}
            selfUid={selfUid}
            onSetSeat={onSetSeat}
            onMoveSelf={onMoveSelf}
          />
        ))}
      </div>
    </div>
  );
}

function SeatCard({
  index, teamId, occupant, isHost, busy, selfUid, onSetSeat, onMoveSelf,
}: {
  index: 0 | 1 | 2 | 3;
  teamId: "A" | "B";
  occupant: SeatOccupant | null;
  isHost: boolean;
  busy: boolean;
  selfUid: string;
  onSetSeat: (i: 0 | 1 | 2 | 3, kind: "bot" | "empty", difficulty?: Difficulty) => void;
  onMoveSelf: (i: 0 | 1 | 2 | 3) => void;
}) {
  const style = TEAM_STYLES[teamId];
  const self = occupant?.uid === selfUid;
  const canClaim = !self && (occupant === null || !occupant.uid); // empty or bot

  const tileClass = occupant?.uid
    ? style.tile
    : occupant?.botDifficulty
      ? "bg-white/5 border-white/15"
      : style.tileEmpty;

  return (
    <div className={"rounded-xl border p-3.5 " + tileClass}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-xs uppercase tracking-wide text-white/50">Seat {index}</div>
        {self && <span className="text-[10px] text-emerald-300 font-semibold">you</span>}
      </div>

      {occupant ? (
        <div>
          <div className="font-semibold truncate">{occupant.screenName}</div>
          <div className="text-xs text-white/50">
            {occupant.uid ? (occupant.botDifficulty ? "—" : "Human") : "Bot"}
          </div>
          {isHost && !occupant.uid && (
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={() => onSetSeat(index, "empty")}
                disabled={busy}
                className="text-xs text-white/60 hover:text-white"
              >
                Clear
              </button>
            </div>
          )}
          {canClaim && (
            <button
              onClick={() => onMoveSelf(index)}
              disabled={busy}
              className="mt-3 w-full text-xs bg-white/10 hover:bg-white/20 rounded-lg px-3 py-1.5 disabled:opacity-40"
            >
              Sit here →
            </button>
          )}
        </div>
      ) : (
        <div>
          <div className="text-white/50 text-sm mb-3">Empty</div>
          <div className="flex flex-col gap-2">
            {canClaim && (
              <button
                onClick={() => onMoveSelf(index)}
                disabled={busy}
                className="text-xs bg-white/10 hover:bg-white/20 rounded-lg px-3 py-1.5 disabled:opacity-40"
              >
                Sit here →
              </button>
            )}
            {isHost && (
              <button
                type="button"
                onClick={() => onSetSeat(index, "bot", "advanced")}
                disabled={busy}
                title="Same bot behavior as solo quick play (standard bidding and play)."
                className="text-xs rounded px-3 py-1.5 bg-white/10 border border-white/20 hover:bg-white/20 disabled:opacity-40 w-full sm:w-auto text-left sm:text-center"
              >
                Add bot
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
